import { useState } from "react";

import { ApiError } from "../api/errors";
import type { OrderDetail, OrderStatus } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { CAP } from "../auth/capabilities";
import { ConfirmDialog } from "../products/ConfirmDialog";
import { ORDER_STATUS_LABELS, TRANSITION_COPY } from "./orderStatus";
import { useOrderTransition } from "./useOrders";

/**
 * The actions an operator may take on this order — and only those.
 *
 * WHAT DECIDES WHICH BUTTONS EXIST
 * `order.allowed_transitions`, sent by the backend for this exact order in its
 * exact current state. This component maps each of those to a label and a
 * confirmation and renders nothing else. It does not know that a placed order
 * can be confirmed, that a fulfilled one is finished, or that cancellation is
 * reachable from three states: those facts live in `ALLOWED_TRANSITIONS` in
 * `backend/app/services/order_lifecycle.py`, which is the one place they are
 * written down and the same place the transition is validated against.
 *
 * That is not merely tidy. A second copy in the browser would drift, and the
 * way it would show up is a button that is offered and then refused — or, far
 * worse, an action quietly missing from a screen with no indication that the
 * shop could have taken it.
 *
 * A STALE SCREEN CANNOT DO DAMAGE
 * The backend re-validates every transition against the row it locks, so a tab
 * left open since before somebody else confirmed this order is refused with a
 * 409 rather than believed. What this component does with that refusal is
 * explain it and show the reloaded state — never resend.
 */
export function OrderTransitionActions({ order }: { order: OrderDetail }) {
  const { can } = useAuth();
  const transition = useOrderTransition(order.id);
  const [pending, setPending] = useState<OrderStatus | null>(null);
  const [reason, setReason] = useState("");
  const [outcome, setOutcome] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const busy = transition.isPending;

  function open(target: OrderStatus) {
    setOutcome(null);
    setError(null);
    setReason("");
    setPending(target);
  }

  function close() {
    setPending(null);
    setReason("");
  }

  function confirm() {
    if (!pending) return;
    const trimmed = reason.trim();
    // The backend requires it too, and refuses without it. Checked here as
    // well so the operator is told before a round trip, not after one.
    if (!trimmed) return;

    transition.mutate(
      { target: pending, reason: trimmed },
      {
        onSuccess: (result) => {
          close();
          setError(null);
          setOutcome(
            result.transition.previous_status === result.transition.new_status
              ? `This order was already ${ORDER_STATUS_LABELS[
                  result.transition.new_status
                ].toLowerCase()}. Nothing changed.`
              : `Moved from ${ORDER_STATUS_LABELS[
                  result.transition.previous_status
                ].toLowerCase()} to ${ORDER_STATUS_LABELS[
                  result.transition.new_status
                ].toLowerCase()}.`,
          );
        },
        onError: (caught) => {
          // The dialog closes on a conflict so the operator is looking at the
          // reloaded order rather than at a confirmation for an action that is
          // no longer available. Everything else keeps the dialog, because the
          // same press is worth making again once the cause is fixed.
          if (caught instanceof ApiError && caught.kind === "conflict") close();
          setOutcome(null);
          setError(caught);
        },
      },
    );
  }

  const copy = pending ? TRANSITION_COPY[pending] : null;

  // Moving an order can return stock to the shelf, so it is its own authority.
  // An administrator who may only READ orders is shown the order and no way to
  // move it — the API refuses the call regardless; this is what stops them
  // being offered a button that always fails.
  if (!can(CAP.ordersTransition)) {
    return (
      <p className="orders__terminal" data-testid="order-transitions-denied">
        You don&apos;t have permission to move orders.
      </p>
    );
  }

  return (
    <div className="stack stack--tight">
      {order.allowed_transitions.length > 0 ? (
        <div className="row row--wrap">
          {order.allowed_transitions.map((target) => (
            <button
              key={target}
              type="button"
              className={
                TRANSITION_COPY[target].destructive
                  ? "btn btn--danger"
                  : "btn btn--primary"
              }
              // Disabled for the whole time a transition is in flight, not just
              // the one being run: two orders' worth of stock can move on one
              // double-press otherwise.
              disabled={busy}
              data-testid={`order-transition-${target}`}
              onClick={() => open(target)}
            >
              {TRANSITION_COPY[target].button}
            </button>
          ))}
        </div>
      ) : (
        <p className="orders__terminal" data-testid="order-no-actions">
          This order is {ORDER_STATUS_LABELS[order.status].toLowerCase()} and
          cannot be moved again. A correction is made with a new order, so the
          record of what happened stays intact.
        </p>
      )}

      {outcome ? (
        <div className="notice notice--success" role="status">
          {outcome}
        </div>
      ) : null}

      {error ? <TransitionError error={error} /> : null}

      {pending && copy ? (
        <ConfirmDialog
          title={copy.title}
          confirmLabel={copy.confirm}
          destructive={copy.destructive}
          busy={busy}
          confirmDisabled={!reason.trim()}
          onCancel={close}
          onConfirm={confirm}
        >
          <p>{copy.body}</p>
          <p className="orders__dialogOrder">
            Order <strong>{order.order_number}</strong> for{" "}
            {order.customer_name}.
          </p>
          <label className="orders__reasonLabel" htmlFor="transition-reason">
            Why? This is recorded in the order&rsquo;s history.
          </label>
          <textarea
            id="transition-reason"
            className="orders__reason"
            rows={3}
            maxLength={500}
            autoFocus
            placeholder={copy.reasonPlaceholder}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

/**
 * Each failure gets its own sentence, because each implies a different next
 * step. None of them shows a status code, a stack trace or anything the server
 * wrote for its own logs.
 */
function TransitionError({ error }: { error: unknown }) {
  if (!(error instanceof ApiError)) {
    return (
      <div className="notice notice--error" role="alert">
        That action could not be completed.
      </div>
    );
  }

  if (error.kind === "conflict") {
    const explanation =
      error.code === "order_state_conflict"
        ? "This order changed somewhere else, so nothing was applied. The current state has been reloaded — check it and decide again."
        : error.code === "idempotency_conflict"
          ? "That request had already been used for a different change. Nothing was applied. Reload and try again."
          : error.code === "idempotency_in_progress"
            ? "An earlier attempt at this is still being processed. Wait a moment, then reload to see what it did."
            : error.userMessage;
    return (
      <div className="notice notice--warning" role="alert">
        {explanation}
      </div>
    );
  }

  if (error.kind === "not_found") {
    return (
      <div className="notice notice--error" role="alert">
        This order no longer exists. Go back to Orders for the current list.
      </div>
    );
  }

  if (error.kind === "forbidden") {
    return (
      <div className="notice notice--error" role="alert">
        That request was rejected for security reasons and nothing was changed.
        Reload the page and try again.
      </div>
    );
  }

  if (error.kind === "validation") {
    return (
      <div className="notice notice--error" role="alert">
        {error.userMessage}
      </div>
    );
  }

  // Unauthenticated is handled globally — the query client clears the session
  // and the login screen takes over — so what is left is a server failure or a
  // request that never completed, and both are worth trying again.
  return (
    <div className="notice notice--error" role="alert">
      {error.userMessage}
    </div>
  );
}
